import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useI18n } from "../../../i18n/I18nContext";
import Footer from "../Footer/Footer";
import { Helmet } from "react-helmet-async";


function useSectionScrollProgress() {
  const ref = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const handleScroll = () => {
      const rect = el.getBoundingClientRect();
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;
      const total = rect.height + windowHeight;
      const offset = windowHeight - rect.top;

      setProgress(Math.min(1, Math.max(0, offset / total)));
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  return { ref, progress };
}

export default function Landing() {
  const { t } = useI18n();

  const beneficios = [
    {
      title: t("Estrutura robusta"),
      text: t("Estantes metálicas em aço de alta resistência, preparadas para cargas pesadas em armazéns, garagens e oficinas."),
    },
    {
      title: t("Montagem simples"),
      text: t("Sistema de encaixe rápido, pensado para uma instalação prática sem ferramentas especializadas."),
    },
    {
      title: t("Modular e escalável"),
      text: t("Adiciona níveis, alarga módulos e adapta a solução de armazenagem à evolução do teu espaço."),
    },
    {
      title: t("Apoio especializado"),
      text: t("Ajudamos a escolher racks metálicos, estantes de carga média ou cantilever conforme o tipo de utilização."),
    },
  ];

  const categorias = [
    {
      title: t("Estantes de Carga Média"),
      text: t("Estantes metálicas de carga média para armazéns, oficinas e garagens, ideais para organizar caixas, ferramentas e materiais pesados."),
      link: t("Ver estantes de carga média →"),
      image: "/porta-palete.jpg",
      alt: t("Estantes metálicas de carga média para armazém, oficina e garagem"),
      to: "/products",
      points: [
        t("Capacidade de carga até 4000kg"),
        t("Montagem simples e rápida"),
        t("Aproveitamento eficiente do espaço"),
      ],
    },
    {
      title: t("Estantes Cantilever"),
      text: t("Estantes cantilever para armazenar madeira, tubos, perfis metálicos e materiais compridos de forma segura e acessível."),
      link: t("Ver estantes cantilever →"),
      image: "/Cantilever Ligeiro Acesso Simples ISO.jpg",
      alt: t("Estantes cantilever para madeira, tubos e perfis metálicos"),
      to: "/products",
      points: [
        t("Face simples ou dupla"),
        t("Braços ajustáveis"),
        t("Ideais para materiais compridos"),
      ],
    },
    {
      title: t("Soluções de Armazenagem à Medida"),
      text: t("Soluções personalizadas de armazenagem para empresas, armazéns e espaços profissionais com necessidades específicas."),
      link: t("Pedir solução personalizada →"),
      image: "/solucao-personalizada.jpg",
      alt: t("Soluções personalizadas de armazenagem para empresas e armazéns"),
      to: "/contact",
      points: [
        t("Design personalizado"),
        t("Instalação profissional"),
        t("Acompanhamento especializado"),
      ],
    },
  ];

  const faqs = [
    {
      question: t("Que tipo de rack devo escolher para uma garagem?"),
      answer:
        t("Para garagens, normalmente recomendamos racks metálicos modulares ou estantes de carga média, porque permitem organizar ferramentas, caixas e materiais com boa capacidade de carga."),
    },
    {
      question: t("As estantes metálicas são fáceis de montar?"),
      answer:
        t("Sim. Muitas soluções usam sistemas de encaixe rápido, permitindo uma montagem simples sem ferramentas especializadas."),
    },
    {
      question: t("Qual é a diferença entre estantes de carga média e cantilever?"),
      answer:
        t("As estantes de carga média são indicadas para caixas, ferramentas e materiais diversos. As estantes cantilever são mais adequadas para materiais longos, como madeira, tubos e perfis metálicos."),
    },
    {
      question: t("A RacksForAll fornece soluções para empresas?"),
      answer:
        t("Sim. A RacksForAll disponibiliza soluções de armazenagem para empresas, armazéns, oficinas, garagens e espaços profissionais em Portugal."),
    },
  ];

  const { ref: beneficiosRef, progress: beneficiosProgress } = useSectionScrollProgress();

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "RacksForAll",
    url: "https://racksforall.eu",
    logo: "https://racksforall.eu/logo.png",
    description:
      "Loja online de racks metálicos, estantes industriais, estantes de carga média, cantilever e soluções de armazenagem para armazéns, garagens e espaços profissionais.",
    areaServed: {
      "@type": "Country",
      name: "Portugal",
    },
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "RacksForAll",
    url: "https://racksforall.eu",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://racksforall.eu/products?search={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Helmet>
        <title>{t("RacksForAll - Racks Metálicos para Armazéns e Garagens")}</title>

        <meta
          name="description"
          content={t("Compre racks metálicos, estantes de carga média, cantilever e soluções de armazenagem para armazéns, garagens, oficinas e espaços profissionais em Portugal.")}
        />

        <link rel="canonical" href="https://racksforall.eu/" />

        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Racksforall",
            "url": "https://racksforall.pt",
            "logo": "https://racksforall.pt/logo.png",
            "telephone": "+351 91 365 07 24",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Estrada Nacional 1, Edifício Sol, 37, 2ºA",
              "addressLocality": "Batalha",
              "postalCode": "2440-489",
              "addressCountry": "PT"
            },
            "sameAs": [
              "https://www.youtube.com/@racksforall20",
              "https://www.linkedin.com/company/racksforall",
              "https://www.instagram.com/racksforall.eu",
              "https://www.facebook.com/profile.php?id=61577909521947"
            ],
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "customer service",
              "availableLanguage": "Portuguese"
            }
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      {/* HERO */}
      <section className="bg-indigo-700 pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <span className="inline-block text-xs font-semibold uppercase tracking-widest text-indigo-200 mb-4 bg-white/10 px-3 py-1 rounded-full">
              {t("Soluções de armazenagem em Portugal")}
            </span>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-5 leading-tight">
              {t("Racks Metálicos e Estantes Industriais para Armazéns e Garagens")}
            </h1>

            <p className="text-base text-indigo-100/80 mb-8 max-w-lg">
              {t("Organiza o teu espaço com racks metálicos, estantes industriais, estantes de carga média e soluções de armazenagem robustas, fáceis de montar e com entrega rápida em Portugal.")}
            </p>

            <div className="flex flex-wrap gap-3">
              <Link
                to="/products"
                className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-white text-indigo-700 text-sm font-bold shadow hover:bg-indigo-50 transition"
              >
                {t("Ver racks metálicos")}
              </Link>

              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-6 py-3 rounded-xl border border-white/30 text-sm font-semibold text-white hover:bg-white/10 transition"
              >
                {t("Pedir orçamento para armazenagem")}
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { bold: "+200", text: t("Projetos concluídos") },
                { bold: t("Até 400 kg"), text: t("por nível de carga") },
                { bold: t("Envios rápidos"), text: t("para todo o país") },
              ].map((s) => (
                <div key={s.bold} className="rounded-2xl bg-white/10 border border-white/20 px-3 py-3">
                  <p className="font-bold text-white text-sm">{s.bold}</p>
                  <p className="text-indigo-200/80 text-xs mt-0.5">{s.text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden lg:flex justify-center">
            <div className="relative w-full max-w-md overflow-hidden rounded-3xl border border-white/20 bg-indigo-600 shadow-2xl">
              <div className="relative h-52 w-full overflow-hidden">
                <img
                  src="/racks-modulares.jpg"
                  alt={t("Racks metálicos modulares para armazéns e garagens")}
                  className="h-full w-full object-cover object-center"
                  loading="eager"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-indigo-900/25 via-transparent to-transparent" />
              </div>

              <div className="bg-white px-5 py-4">
                <p className="text-sm font-bold text-gray-900">{t("Racks metálicos modulares")}</p>

                <p className="mt-1 text-xs text-gray-500">
                  {t("Combina alturas, larguras e níveis para criar o sistema ideal para o teu espaço.")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Wave separator */}
        <div className="overflow-hidden leading-none">
          <svg viewBox="0 0 1440 60" className="w-full" preserveAspectRatio="none" style={{ height: 60 }}>
            <path d="M0,40 C360,80 1080,0 1440,40 L1440,60 L0,60 Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* BENEFÍCIOS */}
      <section ref={beneficiosRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              {t("Porque escolher a RacksForAll?")}
            </h2>

            <p className="text-gray-500 max-w-xl mx-auto text-sm sm:text-base">
              {t("Focamo-nos em soluções robustas, fáceis de montar e adaptadas a pequenos negócios, armazéns, garagens e uso profissional.")}
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {beneficios.map((card, index) => {
              const slice = 1 / beneficios.length;
              const start = slice * index;
              const end = slice * (index + 1);
              const local =
                beneficiosProgress <= start
                  ? 0
                  : beneficiosProgress >= end
                    ? 1
                    : (beneficiosProgress - start) / (end - start);

              return (
                <div
                  key={card.title}
                  className="relative rounded-2xl border border-gray-100 bg-white shadow-sm hover:shadow-md px-5 py-6 transition-all duration-200 overflow-hidden group"
                >
                  <div className="absolute inset-x-0 top-0 h-[3px] bg-gray-100">
                    <div
                      className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-[width] duration-150 ease-out"
                      style={{ width: `${local * 100}%` }}
                    />
                  </div>

                  <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center mb-4 mt-2 group-hover:bg-indigo-100 transition-colors">
                    <div className="w-3 h-3 rounded-full bg-indigo-500" />
                  </div>

                  <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">{card.title}</h3>

                  <p className="text-xs sm:text-sm text-gray-500 leading-relaxed">{card.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TEXTO SEO */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
            {t("Soluções de armazenagem para armazéns, garagens e oficinas")}
          </h2>

          <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
            {t("A RacksForAll disponibiliza racks metálicos, estantes industriais, estantes de carga média, estantes cantilever e soluções personalizadas para organizar materiais, ferramentas, caixas e equipamentos. As nossas soluções são indicadas para empresas, armazéns, oficinas, garagens e espaços profissionais que precisam de melhorar a organização, segurança e aproveitamento do espaço.")}
          </p>
        </div>
      </section>

      {/* CATEGORIAS */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              {t("Racks Metálicos, Estantes de Carga e Soluções de Armazenagem")}
            </h2>

            <p className="text-gray-500 max-w-xl mx-auto text-sm sm:text-base">
              {t("Desde garagens particulares a armazéns industriais, temos soluções para diferentes tipos de carga, materiais e dimensões.")}
            </p>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 items-stretch">
            {categorias.map((card) => (
              <div
                key={card.title}
                className="group h-full overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl flex flex-col"
              >
                <div className="relative h-56 shrink-0 overflow-hidden bg-gray-100">
                  <img
                    src={card.image}
                    alt={card.alt}
                    className="h-full w-full object-cover object-center transition duration-500 group-hover:scale-105"
                    loading="lazy"
                  />

                  <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/20 to-transparent" />
                </div>

                <div
                  className="flex flex-1 flex-col p-7 text-white"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(59,130,246,1) 0%, rgba(99,102,241,1) 100%)",
                  }}
                >
                  <h3 className="mb-2 text-base font-semibold">{card.title}</h3>

                  <p className="mb-4 text-sm text-white/80">{card.text}</p>

                  <ul className="mb-5 space-y-2 text-sm text-white/85">
                    {card.points.map((point) => (
                      <li key={point} className="flex items-start gap-2">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-white/70 shrink-0" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to={card.to}
                    className="mt-auto inline-flex items-center justify-center rounded-xl border border-white/20 bg-white/10 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/20"
                  >
                    {card.link}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              {t("Perguntas frequentes sobre racks metálicos")}
            </h2>

            <p className="text-gray-500 text-sm sm:text-base">
              {t("Respostas rápidas para escolher a solução de armazenagem certa para armazéns, garagens e espaços profissionais.")}
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq) => (
              <div key={faq.question} className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-2">
                  {faq.question}
                </h3>

                <p className="text-sm text-gray-600 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
            {t("Pronto para montar o teu sistema de racks?")}
          </h2>

          <p className="text-gray-500 mb-8 text-sm sm:text-base">
            {t("Explora a nossa loja online ou fala connosco para um aconselhamento rápido sobre medidas, carga e configuração ideal.")}
          </p>

          <div className="flex flex-wrap gap-3 justify-center">
            <Link
              to="/products"
              className="px-8 py-3 rounded-xl bg-indigo-600 text-white font-semibold text-sm hover:bg-indigo-700 shadow-sm transition"
            >
              {t("Ver racks e estantes metálicas")}
            </Link>

            <Link
              to="/contact"
              className="px-8 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition"
            >
              {t("Pedir orçamento personalizado")}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
